import { useState, useEffect, useRef, useMemo } from 'react';
import { Search } from 'lucide-react';
import { useColorHistory } from '../../hooks/useColorHistory';
import { useColorFavorites } from '../../hooks/useColorFavorites';
import { colorEngine } from '../../lib/colorEngine';

interface ColorPickerPopoverProps {
  onSelect: (hex: string) => void;
  currentHex?: string;
  onClose: () => void;
}

export function ColorPickerPopover({ onSelect, currentHex, onClose }: ColorPickerPopoverProps) {
  const { history } = useColorHistory(12);
  const { favorites } = useColorFavorites();
  const [searchQuery, setSearchQuery] = useState('');

  const containerRef = useRef<HTMLDivElement>(null);

  // 4.7 — Click-outside detection
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  // 4.5 — Named color search
  const namedColorResults = useMemo(() => {
    if (!searchQuery.trim()) return [];
    return colorEngine.searchNamedColors(searchQuery.trim(), { limit: 20 });
  }, [searchQuery]);

  const normalizeHex = (hex: string) => hex.toLowerCase();

  const isCurrentColor = (hex: string) =>
    currentHex && normalizeHex(hex) === normalizeHex(currentHex);

  // 4.3 — Recent colors (6×2 grid from history)
  const recentColors = useMemo(
    () => history.slice(0, 12).map((h) => h.hex),
    [history],
  );

  // 4.4 — Favorite colors
  const favColors = useMemo(
    () => favorites.map((f) => f.hex),
    [favorites],
  );

  return (
    <div
      ref={containerRef}
      style={{
        position: 'absolute',
        top: '100%',
        left: 0,
        zIndex: 50,
        maxHeight: '360px',
        overflowY: 'auto',
      }}
      className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl p-3 w-64"
    >
      {/* Recent Colors */}
      {recentColors.length > 0 && (
        <div className="mb-3">
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Recent Colors</p>
          <div className="grid grid-cols-6 gap-1.5">
            {recentColors.map((hex) => (
              <button
                key={hex}
                type="button"
                onClick={() => onSelect(hex)}
                className="w-8 h-8 rounded border transition-transform hover:scale-110"
                style={{
                  backgroundColor: hex,
                  borderColor: isCurrentColor(hex)
                    ? 'var(--accent, #3b82f6)'
                    : 'var(--line, #d1d5db)',
                  borderWidth: isCurrentColor(hex) ? '2px' : '1px',
                  boxShadow: isCurrentColor(hex) ? '0 0 0 2px var(--accent-soft, rgba(59,130,246,0.2))' : 'none',
                }}
                title={hex}
              />
            ))}
          </div>
        </div>
      )}

      {/* Favorite Colors */}
      {favColors.length > 0 && (
        <div className="mb-3">
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Favorites</p>
          <div className="grid grid-cols-6 gap-1.5">
            {favColors.map((hex) => (
              <button
                key={hex}
                type="button"
                onClick={() => onSelect(hex)}
                className="w-8 h-8 rounded border transition-transform hover:scale-110"
                style={{
                  backgroundColor: hex,
                  borderColor: isCurrentColor(hex)
                    ? 'var(--accent, #3b82f6)'
                    : 'var(--line, #d1d5db)',
                  borderWidth: isCurrentColor(hex) ? '2px' : '1px',
                  boxShadow: isCurrentColor(hex) ? '0 0 0 2px var(--accent-soft, rgba(59,130,246,0.2))' : 'none',
                }}
                title={hex}
              />
            ))}
          </div>
        </div>
      )}

      {/* Named Color Search */}
      <div className="mb-2">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search named colors…"
            className="w-full pl-7 pr-2 py-1.5 text-xs border border-gray-200 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Named color search results */}
      {namedColorResults.length > 0 && (
        <div className="max-h-48 overflow-y-auto space-y-0.5">
          {namedColorResults.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => onSelect(item.hex)}
              className="w-full flex items-center gap-2 px-2 py-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-left transition-colors"
            >
              <div
                className="w-5 h-5 rounded border flex-shrink-0"
                style={{
                  backgroundColor: item.hex,
                  borderColor: isCurrentColor(item.hex)
                    ? 'var(--accent, #3b82f6)'
                    : 'var(--line, #d1d5db)',
                  borderWidth: isCurrentColor(item.hex) ? '2px' : '1px',
                }}
              />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-900 dark:text-gray-100 truncate">{item.displayName}</p>
              </div>
              <span className="text-[10px] text-gray-400 font-mono">{item.hex}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
